import React, { useState, useEffect, useRef } from 'react';
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    doc,
    getDoc
} from 'firebase/firestore';
import { db } from '../../../Services/firebase-config';
import { useAuth } from '../../../Contexts/AuthContext';
import { Send, Users, Clock, ArrowLeft, Award } from 'lucide-react';

const TeamChat = ({ teamId, onBack }) => {
    const { currentUser, userData } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [teamData, setTeamData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    // Scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Fetch team data
    useEffect(() => {
        if (!teamId) return;

        const fetchTeamData = async () => {
            try {
                const teamDoc = await getDoc(doc(db, 'hackathonTeams', teamId));
                if (teamDoc.exists()) {
                    setTeamData(teamDoc.data());
                }
            } catch (error) {
                console.error('Error fetching team data:', error);
            }
        };

        fetchTeamData();
    }, [teamId]);

    // Set up real-time listener for messages
    useEffect(() => {
        if (!teamId) return;

        const messagesRef = collection(db, 'hackathonTeams', teamId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messagesList = [];
            snapshot.forEach((doc) => {
                messagesList.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setMessages(messagesList);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [teamId]);

    // Send message function
    const sendMessage = async (e) => {
        e.preventDefault();

        if (!newMessage.trim() || !currentUser || sending) return;

        setSending(true);

        try {
            const messagesRef = collection(db, 'hackathonTeams', teamId, 'messages');

            // Determine sender name based on user role
            let senderName = currentUser.displayName || currentUser.email;
            if (userData?.role === 'Mentor') {
                senderName = `Mentor - ${currentUser.displayName || currentUser.email}`;
            } else if (userData?.role && ['Administrator', 'Admin', 'admin'].includes(userData.role)) {
                senderName = `Admin`;
            }

            await addDoc(messagesRef, {
                text: newMessage.trim(),
                senderId: currentUser.uid,
                senderName: senderName,
                senderEmail: currentUser.email,
                senderPhotoURL: currentUser.photoURL || '',
                userRole: userData?.role || 'Member',
                timestamp: serverTimestamp(),
                createdAt: new Date().toISOString()
            });

            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;

        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if user is team member or mentor
    const isTeamMember = teamData?.memberIds?.includes(currentUser?.uid);
    const isMentor = teamData?.mentorId === currentUser?.uid;
    const isAdmin = userData?.role && ['administrator', 'admin'].includes(userData.role.toString().toLowerCase());
    const canAccessChat = isTeamMember || isMentor || isAdmin;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="text-center p-8">
                <p className="text-gray-600">Please log in to access team chat.</p>
            </div>
        );
    }

    if (!canAccessChat) {
        return (
            <div className="text-center p-8">
                <p className="text-gray-600">You don't have access to this team chat.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[85vh] w-full">
            {/* Header with Back Button */}
            <div className="flex items-center justify-between p-4 bg-custom-blue-1 text-white">
                <div className="flex items-center space-x-4">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                        >
                            <ArrowLeft size={20} />
                        </button>
                    )}
                    <div className="flex items-center space-x-3">
                        <div className="bg-white bg-opacity-20 p-2 rounded-full">
                            <Users size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold">Team Chat</h2>
                            <p className="text-sm opacity-90">
                                {(teamData?.members?.length || 0) + (teamData?.mentor ? 1 : 0)} participants
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Team Members & Mentor Section */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 border-b border-gray-200">
                <div className="space-y-4">
                    {/* Team Members */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Users size={16} className="mr-2" />
                            Team Members
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {teamData?.members?.map((member) => (
                                <div
                                    key={member.id}
                                    className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg text-sm shadow-sm hover:shadow-md transition-shadow"
                                >
                                    {member.photoURL ? (
                                        <img
                                            src={member.photoURL}
                                            alt={member.name}
                                            className="w-6 h-6 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-6 h-6 bg-custom-blue-1 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                                            {member.name?.charAt(0)?.toUpperCase()}
                                        </div>
                                    )}
                                    <span className="font-medium text-gray-800">{member.name}</span>
                                    {member.id === currentUser?.uid && (
                                        <span className="text-custom-green-1 text-xs font-semibold">(You)</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mentor Section */}
                    {teamData?.mentor && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                <Award size={16} className="mr-2 text-custom-green-1" />
                                Mentor
                            </h3>
                            <div className="flex items-center space-x-2 bg-custom-green-1 text-white px-4 py-2 rounded-lg text-sm shadow-md w-fit">
                                {teamData.mentor.photoURL ? (
                                    <img
                                        src={teamData.mentor.photoURL}
                                        alt={teamData.mentor.name}
                                        className="w-6 h-6 rounded-full object-cover border-2 border-white"
                                    />
                                ) : (
                                    <div className="w-6 h-6 bg-white text-custom-green-1 rounded-full flex items-center justify-center text-xs font-bold">
                                        {teamData.mentor.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                )}
                                <span className="font-semibold">{teamData.mentor.name}</span>
                                {teamData.mentor.id === currentUser?.uid && (
                                    <span className="text-green-100 text-xs font-semibold">(You)</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-12">
                        <Users size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">No messages yet</p>
                        <p className="text-sm">Start the conversation with your team!</p>
                    </div>
                ) : (
                    messages.map((message) => {
                        const isOwnMessage = message.senderId === currentUser?.uid;
                        const senderIsMentor = teamData?.mentorId === message.senderId || message.userRole === 'Mentor';
                        const senderIsAdmin = message.userRole && ['Administrator', 'Admin', 'admin'].includes(message.userRole);

                        return (
                            <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-sm lg:max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                                    <div className={`relative p-4 rounded-lg shadow-sm ${isOwnMessage
                                        ? 'bg-custom-blue-1 text-white rounded-br-md'
                                        : senderIsMentor
                                            ? 'bg-gray-100 text-gray-800 rounded-bl-md'
                                            : senderIsAdmin
                                                ? 'bg-gray-100 text-gray-800 rounded-bl-md'
                                                : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                                        }`}>
                                        {!isOwnMessage && (
                                            <div className="flex items-center space-x-2 mb-2">
                                                {message.senderPhotoURL ? (
                                                    <img
                                                        src={message.senderPhotoURL}
                                                        alt={message.senderName}
                                                        className="w-6 h-6 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${senderIsMentor
                                                        ? 'bg-white text-gray-700'
                                                        : senderIsAdmin
                                                            ? 'bg-white text-gray-700'
                                                            : 'bg-custom-blue-1 text-white'
                                                        }`}>
                                                        {message.senderName?.charAt(0)?.toUpperCase()}
                                                    </div>
                                                )}
                                                <span className={`text-sm font-semibold ${senderIsMentor ? 'text-gray-700'
                                                    : senderIsAdmin ? 'text-gray-700'
                                                        : 'text-custom-blue-1'
                                                    }`}>
                                                    {message.senderName}
                                                    {senderIsMentor && (
                                                        <span className="ml-1 text-xs bg-white bg-opacity-20 px-2 py-0.5 rounded">
                                                            Mentor
                                                        </span>
                                                    )}
                                                    {senderIsAdmin && (
                                                        <span className="ml-1 text-xs bg-white bg-opacity-20 px-2 py-0.5 rounded">
                                                            Admin
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                            {message.text}
                                        </p>
                                        <div className={`flex items-center justify-end space-x-1 mt-2 text-xs ${isOwnMessage || senderIsMentor || senderIsAdmin ? 'text-white text-opacity-75' : 'text-gray-500'
                                            }`}>
                                            <Clock size={10} />
                                            <span>{formatTimestamp(message.timestamp)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex space-x-3 max-w-4xl mx-auto">
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage(e);
                            }
                        }}
                        placeholder={
                            userData?.role === 'Mentor' ? "Send a message as mentor..."
                                : userData?.role && ['Administrator', 'Admin', 'admin'].includes(userData.role) ? "Send a message as admin..."
                                    : "Type your message..."
                        }
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent transition-all"
                        disabled={sending}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className={`px-6 py-3 rounded-lg flex items-center space-x-2 transition-all ${!newMessage.trim() || sending
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-custom-blue-1 hover:bg-opacity-90 text-white shadow-lg'
                            }`}
                    >
                        <Send size={16} />
                        <span className="hidden sm:inline font-medium">
                            {sending ? 'Sending...' : 'Send'}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TeamChat;